import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { type ViewModelEntity } from '@view-model/domain/view-model';
import { useCallback, useState } from 'react';
import type { ViewModelIndividualPermissionEntries, WorkspaceMemberEntries } from './types';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import { generateDefaultUserIcon } from '@framework/utils';
import type { UserId, ViewModelId, ViewModelIndividualPermissionRoleJSON } from 'balus-common';
import type { UserPublicProfile } from '@user/PublicProfile';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { trpcClient } from '@framework/trpcClient';
import { ViewModelIndividualPermissions } from '@view-model/domain/view-model/ViewModelIndividualPermissions';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
  workspaceMembers: WorkspaceMemberEntries;
  guestUserIds: Set<UserId>;
  individualPermissions: ViewModelIndividualPermissionEntries;
  onAddMember(): void;
  onRemoveMember(): void;
};

export const MemberEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  viewModel,
  workspaceMembers,
  guestUserIds,
  individualPermissions,
  onAddMember,
  onRemoveMember,
}: Props) => {
  const { t } = useTranslation();
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.memberEdit.title', { name: viewModel.name.toString() })}
      submitButton={null}
      cancelButton={
        <Button color="secondary" onClick={handleClose}>
          {t('common.action.close')}
        </Button>
      }
      containerClassName="overflow-scroll"
      containerStyle={{ maxHeight: '50vh' }}
    >
      <div className="flex justify-end p-2">
        <button
          className="rounded border border-solid border-brand px-2 py-1 text-sm text-brand hover:bg-gray-200"
          onClick={onAddMember}
        >
          {t('workspace.individualPermission.addMember')}
        </button>
        <button
          className="ml-2 rounded border border-solid border-red-700 px-2 py-1 text-sm text-red-700 hover:bg-gray-200"
          onClick={onRemoveMember}
        >
          {t('workspace.memberRemove.submitButton')}
        </button>
      </div>

      <div className="text-lg font-bold">{t('workspace.memberEdit.viewModelSection')}</div>
      <div className="px-2 pb-6 pt-2">
        {individualPermissions.length === 0 ? (
          <div className="flex justify-center px-2 py-4">{t('workspace.memberEdit.emptyState')}</div>
        ) : (
          individualPermissions.map(([profile, role]) => (
            <MemberItem
              key={profile.id}
              viewModelId={viewModel.id}
              profile={profile}
              role={role}
              isGuest={guestUserIds.has(profile.id)}
            />
          ))
        )}
      </div>

      <div className="text-lg font-bold">{t('workspace.memberEdit.workspaceSection')}</div>
      <ul className="px-2 pb-6 pt-2">
        {workspaceMembers.map(([profile, role]) => (
          <li key={profile.id} className="flex flex-row items-center py-2">
            <span className="flex size-6 overflow-hidden rounded-full">
              <Icon profile={profile} />
            </span>
            <span className="grow">
              <span className="mx-1 truncate">{profile.name}</span>
              {guestUserIds.has(profile.id) && (
                <span className="m-1 rounded-lg bg-gray-400 px-2 text-xs leading-6">
                  {t('workspace.individualPermission.guest')}
                </span>
              )}
            </span>

            <span className="w-16 justify-end">{t(WorkspaceMemberRole.getLocalStringKey(role))}</span>
          </li>
        ))}
      </ul>
    </GeneralModal>
  );
};

type ItemProps = {
  viewModelId: ViewModelId;
  profile: UserPublicProfile;
  role: ViewModelIndividualPermissionRoleJSON;
  isGuest: boolean;
};

const MemberItem: React.FC<ItemProps> = ({ viewModelId, profile, role, isGuest }: ItemProps) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const role = event.target.value as ViewModelIndividualPermissionRoleJSON;
      try {
        setProcessing(true);
        await trpcClient.viewModel.individualMember.updateMember.mutate({ viewModelId, userId: profile.id, role });
        setProcessing(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        toast.success(t('workspace.memberEdit.roleChangeSuccess'));
      } catch (e) {
        toast.error(t('workspace.memberEdit.roleChangeError'));
        throw e;
      } finally {
        setProcessing(false);
      }
    },
    [profile.id, viewModelId, t]
  );

  return (
    <li className="flex flex-row items-center py-2">
      <span className="flex size-6 overflow-hidden rounded-full">
        <Icon profile={profile} />
      </span>
      <span className="grow">
        <span className="mx-1 truncate">{profile.name}</span>
        {isGuest && (
          <span className="m-1 rounded-lg bg-gray-400 px-2 text-xs leading-6">
            {t('workspace.individualPermission.guest')}
          </span>
        )}
      </span>

      <span className="flex w-30 justify-end">
        {processing ? (
          <span className="mr-2 font-bold leading-6 text-green-700">
            <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
          </span>
        ) : saved ? (
          <span className="mr-2 font-bold leading-6 text-green-700">
            <FontAwesomeIcon icon={faCheck} />
          </span>
        ) : null}

        <select className="form-select w-24 py-1 text-sm" defaultValue={role} onChange={handleChange}>
          {Object.keys(ViewModelIndividualPermissions.LocaleKeys).map((roleKey) => (
            <option key={roleKey} value={roleKey}>
              {t(ViewModelIndividualPermissions.getLocaleKey(roleKey as ViewModelIndividualPermissionRoleJSON))}
            </option>
          ))}
        </select>
      </span>
    </li>
  );
};

type IconProps = {
  profile: UserPublicProfile;
};

const Icon: React.FC<IconProps> = ({ profile }: IconProps) => {
  return profile.imageUrl ? (
    <img className="size-full object-cover" src={profile.imageUrl} alt={profile.name} />
  ) : (
    <div className="size-full object-cover">
      {generateDefaultUserIcon({
        id: profile.id,
        name: profile.name,
        size: 24,
        isAnonymous: profile.isAnonymous,
      })}
    </div>
  );
};
